import * as React from 'react';
import { getErrorMessage } from '@/api/api';
import { chatService } from '@/services/chatService';
import { echoService } from '@/services/echoService';
import { useDraftChatContext } from '@/features/my-echo/context/useDraftChatContext';
import { useUiStore } from '@/store/uiStore';
import { billingRecoveryContext } from '@/utils/billingRecovery';

type VideoStatus = 'none' | 'pending' | 'processing' | 'done' | 'failed';

interface UseMessageVideoReturn {
  videoStatus: VideoStatus;
  videoUrl: string | null;
  videoError: string | null;
  isGenerating: boolean;
  generateVideo: () => Promise<void>;
}

const POLL_INTERVAL_MS = 5000;

function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = Reflect.get(error, 'status');
  return typeof status === 'number' ? status : undefined;
}

/**
 * Manages lipsync video state for a single AI message.
 *
 * When used inside a DraftChatProvider the hook targets the draft test-chat
 * endpoints (in-memory status, text supplied by caller). Outside a provider
 * it targets the regular /chat/messages/{id}/video/* endpoints that require
 * a persisted Message row.
 *
 * @param messageId Message id for live chat, or a client-side temp id for drafts.
 * @param initialVideoUrl Hydrated URL if video was already generated previously.
 * @param initialVideoStatus Hydrated status if video is already in flight.
 * @param messageText Current text of the AI message — required for draft mode
 *   since the backend can't look it up from the Message table.
 */
export function useMessageVideo(
  messageId: string,
  initialVideoUrl?: string,
  initialVideoStatus?: string | null,
  messageText?: string
): UseMessageVideoReturn {
  const { draftId } = useDraftChatContext();
  const isDraftMode = !!draftId;
  const openBillingRecovery = useUiStore((state) => state.openBillingRecovery);

  const [videoStatus, setVideoStatus] = React.useState<VideoStatus>(() => {
    if (
      initialVideoStatus === 'done' ||
      initialVideoStatus === 'pending' ||
      initialVideoStatus === 'processing' ||
      initialVideoStatus === 'failed'
    ) {
      return initialVideoStatus;
    }
    return 'none';
  });

  const [videoUrl, setVideoUrl] = React.useState<string | null>(initialVideoUrl ?? null);
  const [videoError, setVideoError] = React.useState<string | null>(null);

  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = React.useRef(true);

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchStatus = React.useCallback(async () => {
    if (isDraftMode && draftId) {
      const res = await echoService.getDraftMessageVideoStatus(draftId, messageId);
      return {
        video_status: res.video_status,
        video_url: res.video_url,
        video_error: res.video_error,
      };
    }
    const res = await chatService.getMessageVideoStatus(messageId);
    return {
      video_status: res.video_status,
      video_url: res.video_url,
      video_error: res.video_error,
    };
  }, [draftId, isDraftMode, messageId]);

  const startPolling = React.useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) {
        stopPolling();
        return;
      }
      try {
        const res = await fetchStatus();
        if (!mountedRef.current) return;
        if (res.video_status === 'done') {
          if (res.video_url) {
            setVideoStatus('done');
            setVideoUrl(res.video_url);
            setVideoError(null);
          } else {
            setVideoStatus('failed');
            setVideoError('Video generated but URL unavailable');
          }
          stopPolling();
        } else if (res.video_status === 'failed') {
          setVideoStatus('failed');
          setVideoError(res.video_error ?? 'No video for this echo.');
          stopPolling();
        } else {
          const status = res.video_status as VideoStatus;
          if (status === 'pending' || status === 'processing') {
            setVideoStatus(status);
          }
        }
      } catch {
        // Network error — keep polling
      }
    }, POLL_INTERVAL_MS);
  }, [fetchStatus, stopPolling]);

  // Start polling if initially pending/processing (on mount only)
  const initialStatusRef = React.useRef(videoStatus);
  React.useEffect(() => {
    mountedRef.current = true;
    if (initialStatusRef.current === 'pending' || initialStatusRef.current === 'processing') {
      startPolling();
    }
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const generateVideo = React.useCallback(async () => {
    try {
      setVideoStatus('pending');
      setVideoError(null);
      if (isDraftMode && draftId) {
        const text = messageText ?? '';
        await echoService.generateDraftMessageVideo(draftId, messageId, text);
      } else {
        await chatService.generateMessageVideo(messageId);
      }
      startPolling();
    } catch (err: unknown) {
      if (getErrorStatus(err) === 402) {
        openBillingRecovery(billingRecoveryContext(err, 'VIDEO'));
      }
      setVideoError(getErrorMessage(err));
      setVideoStatus('failed');
    }
  }, [draftId, isDraftMode, messageId, messageText, openBillingRecovery, startPolling]);

  const isGenerating = videoStatus === 'pending' || videoStatus === 'processing';

  return { videoStatus, videoUrl, videoError, isGenerating, generateVideo };
}
